import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, ContentCard, LoadingSkeleton, MenuButton, Row, Table } from "@narmi/design_system";
import {
  useLoadingContext,
  useNotificationContext,
} from "cerulean";
import Payee, { ErrorMessage } from "byzantine/src/Payee";
import Filters from "byzantine/src/filters";
import AddPayeeDrawer from "../../transfer/AddPayeeDrawer";
import DeleteDialog from "./DeleteDialog";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import RecipientsGrid from "./RecipientsGrid";
import RecipientsTable from "./RecipientsTable";

import type { RecipientsView } from "./RecipientsTable";

interface PayeeOptionsProps {
  openDeleteDialog: () => void;
  view: RecipientsView;
}

const PayeeOptions = ({ openDeleteDialog, view }: PayeeOptionsProps) => (
  <MenuButton
    triggerIcon={view === "list" ? "more-vertical" : "more-horizontal"}
  >
    <MenuButton.Item
      label="Delete"
      startIcon="trash-2"
      onSelect={openDeleteDialog}
    />
  </MenuButton>
);

interface PayeeCardProps {
  payee: Payee;
  fetchPayees: () => Promise<void>;
  view: RecipientsView;
}

// renders the payee as a card, or as a table row in list view
const PayeeCard = ({ payee, fetchPayees, view }: PayeeCardProps) => {
  const { l10n } = useLocalization();
  const [isDeletePayeeDialogOpen, setIsDeletePayeeDialogOpen] = useState(false);
  const { sendNotification } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const { currentUser } = useCurrentUser();

  const deletePayee = async () => {
    setIsDeletePayeeDialogOpen(false);
    setIsLoading(true);
    try {
      await payee.delete();
      setIsLoading(false);
      sendNotification({
        type: "success",
        text: l10n.getString("notification-payee-deleted"),
      });
      await fetchPayees();
    } catch (error: unknown) {
      const e = error as ErrorMessage;
      let toastMessage = l10n.getString("error-payee-delete");
      if (e?.reason?.toLowerCase() === Payee.ERROR.PENDING_PAYMENTS_EXIST) {
        toastMessage = l10n.getString("error-payee-delete-pending-payments");
      }
      sendNotification({
        type: "negative",
        text: toastMessage,
      });
      setIsLoading(false);
    }
  };

  const hasPayeePermission =
    currentUser?.features?.["integrations.base.Payee_permission"];
  const payeeAccount = payee.isPersonType()
    ? payee?.email
    : payee?.masked_account_number;

  return (
    <>
      {view === "list" ? (
        <Table.Row>
          <Table.Cell>
            <div className="fontColor--heading">
              {Filters.titlecase(payee.name)}
            </div>
          </Table.Cell>
          <Table.Cell>{payeeAccount}</Table.Cell>
          <Table.Cell>
            <Row justifyContent="center">
              {hasPayeePermission && (
                <PayeeOptions
                  openDeleteDialog={() => setIsDeletePayeeDialogOpen(true)}
                  view={view}
                />
              )}
            </Row>
          </Table.Cell>
        </Table.Row>
      ) : (
        <ContentCard kind="elevated">
          <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Row>
              <Row.Item>
                <div className="fontColor--heading fontWeight--semibold whiteSpace--truncate--multiLine--2">
                  {Filters.titlecase(payee.name)}
                </div>
              </Row.Item>
              {hasPayeePermission && (
                <Row.Item shrink>
                  <div style={{ marginTop: "-4px" }}>
                    <PayeeOptions
                      openDeleteDialog={() => setIsDeletePayeeDialogOpen(true)}
                      view={view}
                    />
                  </div>
                </Row.Item>
              )}
            </Row>
            <div
              className="margin--top--xxs margin--bottom--s"
              style={{ height: "20px" }}
            >
              {payeeAccount}
            </div>
            {payee?.description && (
              <div
                className="fontSize--s fontColor--secondary"
                style={{ marginTop: "auto" }}
              >
                {payee.description}
              </div>
            )}
          </div>
        </ContentCard>
      )}
      <DeleteDialog
        recipientName={Filters.titlecase(payee.name)}
        recipientType="payee"
        onDelete={deletePayee}
        isOpen={isDeletePayeeDialogOpen}
        closeDialog={() => setIsDeletePayeeDialogOpen(false)}
      />
    </>
  );
};

const PayeeSection = ({
  view = "grid",
  isCollapsible = false,
}: {
  view?: RecipientsView;
  isCollapsible?: boolean;
}) => {
  const { l10n } = useLocalization();
  const [payees, setPayees] = useState<Payee[]>([]);
  const [isAddPayeeDialogOpen, setIsAddPayeeDialogOpen] = useState(false);
  const [isFetchingPayees, setIsFetchingPayees] = useState(false);
  const { currentUser } = useCurrentUser();

  const { sendNotification } = useNotificationContext();

  const fetchPayees = async (payee?: Payee) => {
    setIsFetchingPayees(true);
    try {
      setPayees((await Payee.fetchPayees(payee)) ?? []);
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("error-payee-fetch"),
      });
    } finally {
      setIsFetchingPayees(false);
    }
  };

  useEffect(() => {
    fetchPayees();
  }, []);

  return (
    <>
      <div className="margin--top--xl margin--bottom--l">
        <Row>
          <Row.Item>
            <div className="fontSize--l fontColor--heading fontWeight--bold">
              {l10n.getString("heading-payees")}
            </div>
          </Row.Item>
          {currentUser?.features?.["integrations.base.Payee_permission"] && (
            <Row.Item shrink>
              <Button
                kind="plain"
                size="m"
                startIcon="plus"
                label={l10n.getString("recipients-add-payee-button")}
                onClick={() => setIsAddPayeeDialogOpen(true)}
              />
            </Row.Item>
          )}
        </Row>
      </div>
      <LoadingSkeleton content="paragraph" isLoading={isFetchingPayees}>
        {view === "list" && payees?.length ? (
          <RecipientsTable
            items={payees}
            columns={[
              {
                label: l10n.getString("label-name"),
                getSortValue: (payee) => payee.name,
              },
              { label: l10n.getString("label-account") },
            ]}
            renderRow={(payee) => (
              <PayeeCard
                key={payee.id}
                payee={payee}
                fetchPayees={fetchPayees}
                view={view}
              />
            )}
          />
        ) : (
          <RecipientsGrid
            items={payees}
            renderItem={(payee) => (
              <PayeeCard
                key={payee.id}
                payee={payee}
                fetchPayees={fetchPayees}
                view={view}
              />
            )}
            emptyMessage={l10n.getString("message-no-payees")}
            isCollapsible={isCollapsible}
          />
        )}
      </LoadingSkeleton>
      <AddPayeeDrawer
        open={isAddPayeeDialogOpen}
        handleClose={() => setIsAddPayeeDialogOpen(false)}
        updatePayees={(newPayee: Payee) => {
          sendNotification({
            type: "success",
            text: l10n.getString("notification-payee-added"),
          });
          fetchPayees(newPayee);
        }}
      />
    </>
  );
};

export default PayeeSection;
